/**
 * PHQ-9 / GAD-7 behavioral-health screener scoring helpers (RND-35692).
 *
 * Pure logic only — question text lives in i18n (`phq9.*`, `gad7.*`) and the
 * one-at-a-time UI lives in the screener step components. Kept dependency-free
 * so scoring, the BH threshold, and the Q9 crisis trigger are unit-testable in
 * isolation.
 *
 * Threshold source: CMS Participation Agreement Appendix C / Payment Targets
 * Table 5 — PHQ-9 >= 10 OR GAD-7 >= 10 (confirmed 2026-07-01). See
 * PRD 11 §5 and the ACCESS Web Registration Technical Design §6.6/§7.
 */

export const PHQ9_QUESTION_COUNT = 9;
export const GAD7_QUESTION_COUNT = 7;

/** Zero-based index of the PHQ-9 suicidality item (Q9), which drives the crisis gate. */
export const PHQ9_Q9_INDEX = PHQ9_QUESTION_COUNT - 1;

/** Minimum baseline score on either instrument to confirm the BH track. */
export const BH_THRESHOLD = 10;

/** Sum of a questionnaire's per-item responses. */
export const sumAnswers = (answers: number[]): number =>
  answers.reduce((total, value) => total + value, 0);

/** BH-track threshold: PHQ-9 >= 10 OR GAD-7 >= 10 (either instrument alone suffices). */
export const meetsBHThreshold = (phq9Score: number, gad7Score: number): boolean =>
  phq9Score >= BH_THRESHOLD || gad7Score >= BH_THRESHOLD;

/** PHQ-9 Q9 crisis gate: any non-zero response to the suicidality item. */
export const isCrisisResponse = (q9Answer: number): boolean => q9Answer >= 1;
